import { PermutLogo } from "assets/logo"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { motion } from "framer-motion"
import { Monitor, Smartphone } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useEffect, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import type { GetWidgetConfigurationResponseData } from "schema"
import type z from "zod"
import type { WidgetPhoneMockupConfigurationFormType } from "."
import { resetPreviewWidget } from "../lib/resetPreviewWidget"

type Props = {
  form: UseFormReturn<WidgetPhoneMockupConfigurationFormType>
  webChatConfig: z.infer<typeof GetWidgetConfigurationResponseData>
  isLoading: boolean
}

const PREVIEW_MESSAGES = {
  user: "Hello! Can you help me with something?",
  assistant: "Of course! I'm here to help. Feel free to ask me anything.",
}

const MODES = {
  desktop: { width: 810, height: 640, label: "Desktop", Icon: Monitor },
  mobile: { width: 390, height: 640, label: "Mobile", Icon: Smartphone },
} as const

const PHONE_SIZE = {
  width: 390,
}

type Mode = keyof typeof MODES

export const IframeWidgetPreview = ({ form, webChatConfig, isLoading }: Props) => {
  const { data: employee } = useAuthEmployeeQuery()
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const sceneRef = useRef<HTMLDivElement | null>(null)
  const [mode, setMode] = useState<Mode>("desktop")
  const [widgetRevision, setWidgetRevision] = useState(0)
  const config = form.watch()

  useEffect(() => {
    if (!import.meta.hot) return
    const handler = () => setWidgetRevision((r) => r + 1)
    import.meta.hot.on("widget-updated", handler)
    return () => import.meta.hot?.off("widget-updated", handler)
  }, [])

  useEffect(() => {
    const wrapper = wrapperRef.current
    const container = containerRef.current
    const scene = sceneRef.current
    if (!wrapper || !container || !scene) return

    const { width, height } = MODES[mode]

    const update = () => {
      const scale = Math.min(1, wrapper.offsetWidth / width)
      container.style.width = `${Math.round(width * scale)}px`
      container.style.height = `${Math.round(height * scale)}px`
      scene.style.transform = `scale(${scale})`
    }

    const ro = new ResizeObserver(update)
    ro.observe(wrapper)
    update()
    return () => ro.disconnect()
  }, [mode])

  useEffect(() => {
    if (!employee || isLoading) return

    const iframe = iframeRef.current
    if (!iframe) return

    const locale = webChatConfig.languages.at(0)?.value ?? "en"
    const previewConfig = {
      isActive: true,
      channel: "Web",
      company: {
        name: employee.company?.name ?? employee.organization.name,
        logo: employee.company?.logo ?? null,
      },
      webChatConfig: {
        ...webChatConfig,
        theme: "simple",
        dialog: {
          backgroundSize: "cover",
          backgroundColor: "transparent",
          backgroundPosition: "center",
        },
      },
      slug: "preview",
      locale,
      autoHide: false,
      mode: "fullscreen",
      isEmail: false,
      previewMessages: PREVIEW_MESSAGES,
    }

    const configJson = JSON.stringify(previewConfig).replace(/<\//g, "<\\/")
    const html = `<!DOCTYPE html><html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>body { margin: 0; padding: 0; }</style>
  <script>window.__PERMUT_PREVIEW_CONFIG__ = ${configJson};<\/script>
</head><body>
  <script src="/widget.js?v=${widgetRevision}"><\/script>
</body></html>`

    const doc = iframe.contentWindow?.document
    if (!doc) return

    resetPreviewWidget(iframe, "iframe-widget-preview")
    doc.open()
    doc.write(html)
    doc.close()
  }, [employee, isLoading, webChatConfig, widgetRevision])

  const { width, height } = MODES[mode]
  const phoneWidth = PHONE_SIZE.width

  return (
    <div className="flex flex-col gap-3">
      <Tabs
        variant="pill"
        value={mode}
        onValueChange={(v) => setMode(v as Mode)}
        className="w-fit self-center"
      >
        <TabsList variant="pill">
          {(Object.keys(MODES) as Mode[]).map((m) => {
            const { label, Icon } = MODES[m]
            return (
              <TabsTrigger key={m} value={m} variant="pill">
                <Icon size={14} />
                {label}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>
      <div ref={wrapperRef} className="w-full flex justify-center overflow-hidden">
        <div
          ref={containerRef}
          className="bg-secondary rounded-2xl border border-border overflow-hidden relative"
        >
          <div
            ref={sceneRef}
            className="relative overflow-hidden origin-top-left"
            style={{
              width,
              height,
            }}
          >
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <PermutLogo className="w-2/3 opacity-[0.07]" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="relative overflow-hidden"
                animate={{
                  width: phoneWidth,
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 28,
                }}
                style={{
                  aspectRatio: config.phone.aspectRatio,
                }}
              >
                <div
                  className="absolute overflow-hidden"
                  style={{
                    inset: config.phone.padding,
                    borderRadius: config.phone.borderRadius,
                    backgroundColor: config.background.color,
                    backgroundImage: config.background.image
                      ? `url(${config.background.image})`
                      : undefined,
                    backgroundPosition: config.background.position,
                    backgroundSize: config.background.size,
                  }}
                >
                  <div
                    className="absolute inset-0 box-border overflow-hidden"
                    style={{
                      padding: config.phone.iframePadding,
                    }}
                  >
                    <div className="relative h-full w-full overflow-hidden">
                      <iframe
                        ref={iframeRef}
                        className="absolute inset-0"
                        style={{
                          width: `100%`,
                          height: `100%`,
                          border: 0,
                          display: "block",
                          transformOrigin: "top left",
                        }}
                        title="Widget iframe preview"
                      />
                    </div>
                  </div>
                </div>
                <img
                  src={config.phone.image}
                  alt=""
                  className="absolute inset-0 block h-full w-full object-contain pointer-events-none"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
